import React, { useCallback, useEffect, useState } from 'react'
import Table from 'component/Table/table'
import Modal from 'component/modal'
import Card from 'component/Card/card'
import Api from 'utils/api'
import ButtonGradient from 'component/Buttons/dashboard-btn'
import SecondaryBtn from 'component/Buttons/secondary-btn'
import InputFieldOutlined from 'component/Inputs/dashboard-input'
import Loader from 'component/loader'
import { Form, Formik } from 'formik'
import * as yup from 'yup'

const Headers = [
  { name: 'Name',   accessor: 'name' },
  { name: 'Email',  accessor: 'email' },
  { name: 'Role',   accessor: 'role' },
  { name: 'Status', accessor: 'status' },
  { name: 'Actions',accessor: 'actions' },
]

const schema = yup.object({
  firstname: yup.string().required().label('First Name'),
  lastname:  yup.string().required().label('Last Name'),
  email:     yup.string().email().required().label('Email'),
  password:  yup.string().required().label('Password'),
})

function MerchantTeam({ location }) {
  const [members, setMembers] = useState([])
  const [roles, setRoles] = useState([])
  const [loading, setLoading] = useState(true)
  const [openModal, setOpenModal] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [selectedRole, setSelectedRole] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    const [membersRes, rolesRes] = await Promise.all([
      Api('/location/team-admin?location_id=' + location._id, 'get'),
      Api('/location-roles?location_id=' + location._id, 'get'),
    ])
    if (membersRes.response?.data) setMembers(membersRes.response.data)
    if (rolesRes.response?.data) setRoles(rolesRes.response.data)
    setLoading(false)
  }, [location._id])

  useEffect(() => { load() }, [load])

  const handleCreate = async (values, { resetForm }) => {
    setProcessing(true)
    const { response } = await Api('/auth/register_account', 'post', {
      payload: {
        ...values,
        businessName: location.name,
        location_role_id: selectedRole || undefined,
      },
      notify: true,
    })
    setProcessing(false)
    if (response) {
      resetForm()
      setOpenModal(false)
      load()
    }
  }

  const handleSuspend = async (userId, active) => {
    await Api('/user/update_user', 'put', { payload: { _id: userId, active }, notify: true })
    setMembers(m => m.map(u => u._id === userId ? { ...u, active } : u))
  }

  const Rows = members.map(m => ({
    name:    <span className='font-medium text-gray-800'>{(m.firstname || '') + ' ' + (m.lastname || '')}</span>,
    email:   <span className='text-sm text-gray-500'>{m.email}</span>,
    role:    <span className='text-xs bg-blue-50 text-blue-700 px-2 py-1 rounded-full'>{m.location_role_id?.name || m.roles?.[0] || '—'}</span>,
    status:  m.active === false
      ? <span className='text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full'>Suspended</span>
      : <span className='text-xs bg-green-100 text-green-600 px-2 py-1 rounded-full'>Active</span>,
    actions: (
      <div className='flex gap-3'>
        {m.active === false
          ? <button onClick={() => handleSuspend(m._id, true)}  className='text-xs text-green-600 hover:underline'>Activate</button>
          : <button onClick={() => handleSuspend(m._id, false)} className='text-xs text-amber-600 hover:underline'>Suspend</button>
        }
      </div>
    ),
  }))

  if (loading) return <Loader />

  return (
    <div>
      <div className='flex items-center justify-between mb-4'>
        <h2 className='text-sm font-semibold text-gray-700'>Team Members ({members.length})</h2>
        <ButtonGradient type='button' onClick={() => setOpenModal(true)}>+ Add Member</ButtonGradient>
      </div>

      {members.length === 0
        ? <p className='text-sm text-gray-400 text-center py-8'>No team members yet.</p>
        : <Table columns={Headers} rows={Rows} />
      }

      <Modal open={openModal} onClose={() => setOpenModal(false)}>
        <Formik validationSchema={schema} initialValues={{ firstname: '', lastname: '', email: '', password: '' }} onSubmit={handleCreate} isInitialValid={false}>
          {({ isValid }) => (
            <Form>
              <Card className='w-[480px]'>
                <div className='flex items-center justify-between p-4 border-b'>
                  <h3 className='text-lg font-semibold text-gray-900'>Add Team Member</h3>
                </div>
                <div className='p-4 space-y-3'>
                  <div className='grid grid-cols-2 gap-3'>
                    <InputFieldOutlined label='First Name' name='firstname' required />
                    <InputFieldOutlined label='Last Name' name='lastname' required />
                  </div>
                  <InputFieldOutlined label='Email' name='email' type='email' required />
                  <InputFieldOutlined label='Password' name='password' type='password' required />
                  <div>
                    <label className='block text-sm font-medium text-gray-700 mb-1'>Role</label>
                    <select
                      value={selectedRole}
                      onChange={e => setSelectedRole(e.target.value)}
                      className='w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-200'
                    >
                      <option value=''>Select role…</option>
                      {roles.map(r => <option key={r._id} value={r._id}>{r.name}{r.predefined ? ' (Predefined)' : ''}</option>)}
                    </select>
                  </div>
                </div>
                <div className='flex items-center gap-2 p-4 border-t'>
                  <ButtonGradient type='submit' processing={processing} disabled={!isValid}>Add Member</ButtonGradient>
                  <SecondaryBtn onClick={() => setOpenModal(false)} label='Cancel' />
                </div>
              </Card>
            </Form>
          )}
        </Formik>
      </Modal>
    </div>
  )
}

export default MerchantTeam
